import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { FileType } from 'src/app/shared/enums/file-type.enum';
import { PureQueue } from 'src/app/shared/enums/pure-queue.enum';
import { EndpointFile } from 'src/app/shared/models/backoffice/endpoint-file.model';
import { MockedFile } from 'src/app/shared/models/backoffice/mocked-file.model';
import { QueueFile } from 'src/app/shared/models/backoffice/queue-cases/queue-file.model';

import { MockedVin } from '../../shared/models/backoffice/mocked-vin.model';
import { ActionPlan } from 'src/app/shared/models/backoffice/action-plan.model';
import { ActionPlanItem } from 'src/app/shared/enums/action-plan-item.enum';

@Injectable()
export class BackofficeService {
  private readonly httpClient = inject(HttpClient);

  private readonly dealershipApiUrl = '/api/dealership';
  private readonly mockingApiUrl = '/api/mocking';

  resetCaseByVin(vin: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.dealershipApiUrl}/internal/cases`, { body: [vin] });  }

  getMockedVinData(vin: string): Observable<MockedVin> {
    return this.httpClient.get<MockedVin>(`${this.mockingApiUrl}/mocked-vin/${vin}`);
  }

  getAllMockedVinData(): Observable<MockedVin[]> {
    return this.httpClient.get<MockedVin[]>(`${this.mockingApiUrl}/mocked-vin`);
  }

  getActionPlan(vin: string): Observable<ActionPlan[]> {
    return this.httpClient.get<ActionPlan[]>(`${this.mockingApiUrl}/mocked-vin/${vin}/action-plan`);
  }

  updateActionPlan(vin: string, actionPlan: ActionPlan[]): Observable<void> {
    return this.httpClient.put<void>(`${this.mockingApiUrl}/mocked-vin/${vin}/action-plan`, actionPlan);
  }

  addMockedVin(vin: string): Observable<void> {
    return this.httpClient.post<void>(`${this.mockingApiUrl}/mocked-vin`, vin);
  }

  deleteMockedVin(vin: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.mockingApiUrl}/mocked-vin/${vin}`);
  }

  addFileToMockedEndpoint(vin: string, endpointId: number, file: File): Observable<EndpointFile> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('filename', file.name);

    return this.httpClient.post<EndpointFile>(
      `${this.mockingApiUrl}/mocked-vin/${vin}/endpoints/${endpointId}/files`,
      formData
    );
  }

  addFileToMockedQueue(
    vin: string,
    file: File,
    pureQueue: PureQueue,
    action?: ActionPlanItem
  ): Observable<QueueFile[]> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('filename', file.name);
    const actionParam = action !== undefined ? `&action=${action}` : '';

    return this.httpClient.post<QueueFile[]>(
      `${this.mockingApiUrl}/mocked-vin/${vin}/queue/files?pureQueue=${pureQueue}${actionParam}`,
      formData
    );
  }

  reOrderQueueFiles(vin: string, from: number, to: number): Observable<void> {
    return this.httpClient.put<void>(`${this.mockingApiUrl}/mocked-vin/${vin}/queue/files`, {
      from,
      to,
    });
  }

  updateMockedEndpoint(vin: string, endpointId: number, isMocked: boolean): Observable<void> {
    return this.httpClient.put<void>(`${this.mockingApiUrl}/mocked-vin/${vin}/endpoints/${endpointId}`, {
      isMocked,
    });
  }

  updateMockedEndpointSelectedFile(vin: string, endpointId: number, fileId: number): Observable<void> {
    return this.httpClient.put<void>(`${this.mockingApiUrl}/mocked-vin/${vin}/endpoints/${endpointId}/files`, {
      id: fileId,
    });
  }

  getFile(vin: string, fileId: number, type: FileType): Observable<MockedFile> {
    return this.httpClient.get<MockedFile>(
      `${this.mockingApiUrl}/mocked-vin/${vin}/mocked-file/${fileId}?type=${type}`
    );
  }

  deleteFile(vin: string, fileId: number, type: FileType): Observable<void> {
    return this.httpClient.delete<void>(`${this.mockingApiUrl}/mocked-vin/${vin}/mocked-file/${fileId}?type=${type}`);
  }
}
